import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
	Badge,
	Box,
	Button,
	Card,
	Group,
	Loader,
	Menu,
	Pagination,
	Paper,
	ScrollArea,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
} from '@mantine/core'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'
import { getExperienceSales } from '../services/actions/experienceActions'
import { toast } from '../services/utils/toast'

const SALES_CSV_FIELDS = [
	'saleId',
	'soldAt',
	'itemName',
	'paymentAmount',
	'subtotalAmount',
	'customerFeeAmount',
	'platformFeeAmount',
	'totalAmount',
	'netAmount',
	'paymentMethod',
	'paymentStatus',
	'sellerName',
	'travelerName',
	'travelerEmail',
	'stationName',
	'issued',
	'checkInId',
	'bookingItemId',
	'orderId',
	'transactionId',
]

const formatMoney = (amountCents) => {
	const cents = Number(amountCents || 0)
	if (!Number.isFinite(cents)) return '$0.00'
	return `$${(cents / 100).toFixed(2)}`
}

const formatDate = (value) => {
	if (!value) return 'Unknown'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return 'Unknown'
	return date.toLocaleString()
}

const PAGE_SIZE = 50

const SalesOverviewPage = () => {
	const { orgSlug, experienceSlug } = useParams()
	const navigate = useNavigate()
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [searchTerm, setSearchTerm] = useState('')
	const [productFilter, setProductFilter] = useState('')
	const [sellerFilter, setSellerFilter] = useState('')
	const [paymentFilter, setPaymentFilter] = useState('')
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
	const [page, setPage] = useState(1)
	const [downloading, setDownloading] = useState(false)

	useEffect(() => {
		const timeoutId = window.setTimeout(
			() => setDebouncedSearchTerm(searchTerm.trim()),
			300,
		)
		return () => window.clearTimeout(timeoutId)
	}, [searchTerm])

	useEffect(() => {
		setPage(1)
	}, [debouncedSearchTerm, paymentFilter, productFilter, sellerFilter])

	useEffect(() => {
		let isCurrent = true
		setLoading(true)
		setError('')

		getExperienceSales(experienceSlug, orgSlug, {
			page,
			pageSize: PAGE_SIZE,
			q: debouncedSearchTerm,
			product: productFilter,
			seller: sellerFilter,
			paymentMethod: paymentFilter,
		})
			.then((result) => {
				if (!isCurrent) return
				setData(result)
			})
			.catch((err) => {
				if (!isCurrent) return
				console.error('Failed to load sales overview:', err)
				setError('Failed to load sales overview.')
			})
			.finally(() => {
				if (isCurrent) setLoading(false)
			})

		return () => {
			isCurrent = false
		}
	}, [
		debouncedSearchTerm,
		experienceSlug,
		orgSlug,
		page,
		paymentFilter,
		productFilter,
		sellerFilter,
	])

	const sales = useMemo(() => data?.sales || [], [data])
	const summary = data?.summary || {}
	const products = useMemo(() => data?.products || [], [data])
	const sellers = useMemo(() => data?.sellers || [], [data])
	const paymentMethods = useMemo(() => data?.paymentMethods || [], [data])
	const pagination = data?.pagination || {
		page: 1,
		pageSize: PAGE_SIZE,
		totalItems: sales.length,
		totalPages: 1,
	}
	const firstVisibleSale = pagination.totalItems
		? (pagination.page - 1) * pagination.pageSize + 1
		: 0
	const lastVisibleSale = Math.min(
		pagination.page * pagination.pageSize,
		pagination.totalItems,
	)

	const productOptions = products.map((product) => ({
		value: product.itemOptionId || product.itemName,
		label: product.itemName,
	}))
	const sellerOptions = sellers.map((seller) => ({
		value: seller.sellerId || seller.sellerName,
		label: seller.sellerName,
	}))
	const paymentOptions = paymentMethods.map((method) => ({
		value: method.paymentMethod,
		label: method.paymentMethod,
	}))

	const topProductChartData = products.slice(0, 8).map((product) => ({
		name: product.itemName,
		revenue: Number(product.revenueCents || 0) / 100,
		units: product.units,
	}))

	const downloadSalesCsv = (downloadSales, fileName) => {
		const rows = downloadSales.map((sale) => ({
			saleId: sale.saleId,
			soldAt: sale.soldAt || '',
			itemName: sale.itemName || '',
			paymentAmount: sale.paymentAmount || '0.00',
			subtotalAmount: sale.subtotalAmount || '0.00',
			customerFeeAmount: sale.customerFeeAmount || '0.00',
			platformFeeAmount: sale.platformFeeAmount || '0.00',
			totalAmount: sale.totalAmount || sale.paymentAmount || '0.00',
			netAmount:
				sale.netAmount || sale.totalAmount || sale.paymentAmount || '0.00',
			paymentMethod: sale.paymentMethod || '',
			paymentStatus: sale.paymentStatus || '',
			sellerName: sale.sellerName || '',
			travelerName: sale.travelerName || '',
			travelerEmail: sale.travelerEmail || '',
			stationName: sale.stationName || '',
			issued: sale.issued ?? false,
			checkInId: sale.checkInId || '',
			bookingItemId: sale.bookingItemId || '',
			orderId: sale.orderId || '',
			transactionId: sale.transactionId || '',
		}))
		const csv =
			rows.length > 0
				? Papa.unparse(rows)
				: Papa.unparse({ fields: SALES_CSV_FIELDS, data: [] })
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		saveAs(blob, fileName)
	}

	const downloadSales = async ({ filtered }) => {
		setDownloading(true)
		try {
			const result = await getExperienceSales(experienceSlug, orgSlug, {
				download: true,
				...(filtered
					? {
							q: searchTerm.trim(),
							product: productFilter,
							seller: sellerFilter,
							paymentMethod: paymentFilter,
						}
					: {}),
			})
			downloadSalesCsv(
				result?.sales || [],
				filtered
					? `${experienceSlug}-sales-results.csv`
					: `${experienceSlug}-sales.csv`,
			)
		} catch (err) {
			console.error('Failed to download sales:', err)
			toast.error({ message: 'Failed to download sales.' })
		} finally {
			setDownloading(false)
		}
	}

	const hasActiveFilters = Boolean(
		searchTerm.trim() || productFilter || sellerFilter || paymentFilter,
	)

	return (
		<Box p='md'>
			<Group position='apart' mb='md'>
				<Box>
					<Title order={2}>Sales Overview</Title>
					<Text size='sm' color='dimmed'>
						Experience Store and on-site sales, payment, fulfillment, and staff attribution.
					</Text>
				</Box>
				<Button
					variant='outline'
					onClick={() =>
						navigate(`/${orgSlug}/experiences/${experienceSlug}/overview`)
					}
				>
					Check-In Overview
				</Button>
			</Group>

			{loading && !data ? (
				<Group position='center' py='xl'>
					<Loader />
				</Group>
			) : error ? (
				<Text color='red'>{error}</Text>
			) : (
				<Stack spacing='md'>
					<SimpleGrid cols={4} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
						<Card withBorder shadow='sm'>
							<Text size='sm' color='dimmed'>
								Gross Sales
							</Text>
							<Title order={2}>{formatMoney(summary.totalAmountCents)}</Title>
						</Card>
						<Card withBorder shadow='sm'>
							<Text size='sm' color='dimmed'>
								Products Added
							</Text>
							<Title order={2}>{summary.totalUnits || 0}</Title>
						</Card>
						<Card withBorder shadow='sm'>
							<Text size='sm' color='dimmed'>
								Paid Items
							</Text>
							<Title order={2}>{summary.paidUnits || 0}</Title>
						</Card>
						<Card withBorder shadow='sm'>
							<Text size='sm' color='dimmed'>
								Issued Items
							</Text>
							<Title order={2}>{summary.issuedUnits || 0}</Title>
						</Card>
					</SimpleGrid>

					<SimpleGrid cols={2} breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
						<Paper withBorder p='md' shadow='xs'>
							<Title order={4} mb='sm'>
								Gross Sales by Product
							</Title>
							{topProductChartData.length > 0 ? (
								<ResponsiveContainer width='100%' height={260}>
									<BarChart data={topProductChartData}>
										<CartesianGrid strokeDasharray='3 3' />
										<XAxis dataKey='name' />
										<YAxis />
										<Tooltip
											formatter={(value, name) =>
												name === 'revenue'
													? formatMoney(Number(value) * 100)
													: value
											}
										/>
										<Bar dataKey='revenue' fill='#228be6' />
									</BarChart>
								</ResponsiveContainer>
							) : (
								<Text size='sm' color='dimmed'>
									No product sales yet.
								</Text>
							)}
						</Paper>

						<Paper withBorder p='md' shadow='xs'>
							<Title order={4} mb='sm'>
								Payment Methods
							</Title>
							<Table highlightOnHover>
								<thead>
									<tr>
										<th>Method</th>
										<th>Items</th>
										<th>Gross Sales</th>
									</tr>
								</thead>
								<tbody>
									{paymentMethods.map((method) => (
										<tr key={method.paymentMethod}>
											<td>{method.paymentMethod}</td>
											<td>{method.units}</td>
											<td>{formatMoney(method.revenueCents)}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</Paper>
					</SimpleGrid>

					<SimpleGrid cols={2} breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
						<Paper withBorder p='md' shadow='xs'>
							<Title order={4} mb='sm'>
								Products
							</Title>
							<Table highlightOnHover>
								<thead>
									<tr>
										<th>Product</th>
										<th>Items</th>
										<th>Gross Sales</th>
										<th>Issued</th>
									</tr>
								</thead>
								<tbody>
									{products.slice(0, 8).map((product) => (
										<tr key={product.itemOptionId || product.itemName}>
											<td>{product.itemName}</td>
											<td>{product.units}</td>
											<td>{formatMoney(product.revenueCents)}</td>
											<td>{product.issuedUnits}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</Paper>

						<Paper withBorder p='md' shadow='xs'>
							<Title order={4} mb='sm'>
								Items Added by Staff
							</Title>
							<Table highlightOnHover>
								<thead>
									<tr>
										<th>Staff</th>
										<th>Items</th>
										<th>Gross Sales</th>
									</tr>
								</thead>
								<tbody>
									{sellers.slice(0, 8).map((seller) => (
										<tr key={seller.sellerId || seller.sellerName}>
											<td>{seller.sellerName}</td>
											<td>{seller.units}</td>
											<td>{formatMoney(seller.revenueCents)}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</Paper>
					</SimpleGrid>

					<Paper withBorder p='md' shadow='xs'>
						<Group position='apart' mb='md'>
							<Title order={4}>Sales</Title>
							<Group>
								{loading && <Loader size='xs' />}
								<Text size='sm' color='dimmed'>
									Showing {firstVisibleSale}–{lastVisibleSale} of{' '}
									{pagination.totalItems}
								</Text>
								{hasActiveFilters ? (
									<Menu position='bottom-end' withinPortal>
										<Menu.Target>
											<Button loading={downloading} rightSection='▾'>
												Download
											</Button>
										</Menu.Target>
										<Menu.Dropdown>
											<Menu.Item onClick={() => downloadSales({ filtered: false })}>
												Download All
											</Menu.Item>
											<Menu.Item onClick={() => downloadSales({ filtered: true })}>
												Download Results
											</Menu.Item>
										</Menu.Dropdown>
									</Menu>
								) : (
									<Button
										loading={downloading}
										onClick={() => downloadSales({ filtered: false })}
									>
										Download
									</Button>
								)}
							</Group>
						</Group>
						<SimpleGrid cols={4} breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
							<TextInput
								label='Search'
								value={searchTerm}
								onChange={(event) => setSearchTerm(event.target.value)}
								placeholder='Item, traveler name, staff name'
							/>
							<Select
								label='Product'
								data={productOptions}
								value={productFilter}
								onChange={(value) => setProductFilter(value || '')}
								placeholder='All products'
								clearable
								withinPortal
							/>
							<Select
								label='Staff'
								data={sellerOptions}
								value={sellerFilter}
								onChange={(value) => setSellerFilter(value || '')}
								placeholder='All staff'
								clearable
								withinPortal
							/>
							<Select
								label='Payment'
								data={paymentOptions}
								value={paymentFilter}
								onChange={(value) => setPaymentFilter(value || '')}
								placeholder='All methods'
								clearable
								withinPortal
							/>
						</SimpleGrid>

						<ScrollArea mt='md'>
							<Table highlightOnHover verticalSpacing='sm'>
								<thead>
									<tr>
										<th>Item</th>
										<th>Gross Amount</th>
										<th>Method</th>
										<th>Staff</th>
										<th>Traveler</th>
										<th>Issued</th>
										<th>Sold</th>
									</tr>
								</thead>
								<tbody>
									{sales.length === 0 && (
										<tr>
											<td colSpan={7}>
												<Text size='sm' color='dimmed' align='center' py='md'>
													No sales match these filters.
												</Text>
											</td>
										</tr>
									)}
									{sales.map((sale) => (
										<tr
											key={sale.saleId}
											onClick={() =>
												navigate(
													`/${orgSlug}/experiences/${experienceSlug}/overview/sales/${encodeURIComponent(
														sale.saleId,
													)}`,
												)
											}
											style={{ cursor: 'pointer' }}
										>
											<td>{sale.itemName}</td>
											<td>{formatMoney(sale.paymentAmountCents)}</td>
											<td>{sale.paymentMethod}</td>
											<td>{sale.sellerName}</td>
											<td>{sale.travelerName}</td>
											<td>
												<Badge color={sale.issued ? 'teal' : 'gray'}>
													{sale.issued ? 'Issued' : 'Not Issued'}
												</Badge>
											</td>
											<td>{formatDate(sale.soldAt)}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</ScrollArea>
						{pagination.totalPages > 1 && (
							<Group position='apart' mt='md'>
								<Text size='sm' color='dimmed'>
									Page {pagination.page} of {pagination.totalPages}
								</Text>
								<Pagination
									value={pagination.page}
									onChange={setPage}
									total={pagination.totalPages}
									size='sm'
								/>
							</Group>
						)}
					</Paper>
				</Stack>
			)}
		</Box>
	)
}

export default SalesOverviewPage
